
// Define errors that can occur during geolocation requests
export const GEO_ERRORS = {
  PERMISSION_DENIED: 'Location permission denied',
  POSITION_UNAVAILABLE: 'Location information is unavailable',
  TIMEOUT: 'Location request timed out',
  UNSUPPORTED: 'Geolocation is not supported by this browser',
  UNKNOWN: 'An unknown error occurred while retrieving location'
};

/**
 * Get the current position using the browser's Geolocation API
 * @returns Promise that resolves with the coordinates or rejects with an error message
 */
export const getCurrentPosition = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(GEO_ERRORS.UNSUPPORTED);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(GEO_ERRORS.PERMISSION_DENIED);
            break;
          case error.POSITION_UNAVAILABLE:
            reject(GEO_ERRORS.POSITION_UNAVAILABLE);
            break;
          case error.TIMEOUT:
            reject(GEO_ERRORS.TIMEOUT);
            break;
          default:
            reject(GEO_ERRORS.UNKNOWN);
        }
      },
      { 
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Convert coordinates to a readable location address using reverse geocoding
 * @param latitude Latitude coordinate
 * @param longitude Longitude coordinate
 * @returns Promise that resolves with location description or rejects with error
 */
import { supabase } from "@/integrations/supabase/client";

export const getAddressFromCoordinates = async (
  latitude: number, 
  longitude: number
): Promise<string> => {
  try {
    console.log(`Getting address for coordinates: ${latitude}, ${longitude}`);
    
    // Use Supabase client to invoke the edge function
    const { data, error } = await supabase.functions.invoke('reversegeocode', {
      body: { lat: latitude, lng: longitude }
    });
    
    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(`Failed to invoke reverse geocoding: ${error.message}`);
    }
    
    if (!data || !data.data || !data.data.formatted_address) {
      console.error('No address found in API response:', data);
      throw new Error('No address found');
    }
    
    console.log('Found address:', data.data.formatted_address);
    return data.data.formatted_address;
  } catch (error) {
    console.error('Error in reverse geocoding:', error);
    // Return approximate coordinates as fallback
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
  }
};
